import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Wallet,
  MoreVertical,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Copy,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Settings,
  RefreshCw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ConnectedWallet,
  WalletBalance,
  WALLET_PROVIDER_INFO,
  SUPPORTED_CHAINS,
} from '../../types/multi-wallet';
import { toast } from 'sonner';

export function WalletList() {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [hiddenBalances, setHiddenBalances] = useState<Set<string>>(new Set());

  // Mock wallets - will be replaced with real connected wallets
  const wallets: (ConnectedWallet & { balance: WalletBalance })[] = [
    {
      id: '1',
      address: '0x1234567890abcdef1234567890abcdef12345678',
      label: 'Main Wallet',
      type: 'metamask',
      chainId: 1,
      isActive: true,
      addedAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
      lastSynced: Date.now() - 5 * 60 * 1000,
      balance: {
        walletId: '1',
        tokens: [],
        totalValueUSD: 45230.50,
        change24h: 1523.45,
        change24hPercent: 3.48,
      },
    },
    {
      id: '2',
      address: '0xabcdef1234567890abcdef1234567890abcdef12',
      label: 'Trading Wallet',
      type: 'thor_wallet',
      chainId: 1,
      isActive: false,
      addedAt: Date.now() - 15 * 24 * 60 * 60 * 1000,
      lastSynced: Date.now() - 10 * 60 * 1000,
      balance: {
        walletId: '2',
        tokens: [],
        totalValueUSD: 28450.20,
        change24h: 2418.27,
        change24hPercent: 8.5,
      },
    },
    {
      id: '3',
      address: '0x9876543210fedcba9876543210fedcba98765432',
      label: 'Cold Storage',
      type: 'ledger',
      chainId: 1,
      isActive: false,
      addedAt: Date.now() - 60 * 24 * 60 * 60 * 1000,
      lastSynced: Date.now() - 2 * 60 * 60 * 1000,
      balance: {
        walletId: '3',
        tokens: [],
        totalValueUSD: 42500.00,
        change24h: -510.00,
        change24hPercent: -1.2,
      },
    },
    {
      id: '4',
      address: '0xfedcba9876543210fedcba9876543210fedcba98',
      label: 'DeFi Farming',
      type: 'walletconnect',
      chainId: 137,
      isActive: false,
      addedAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
      lastSynced: Date.now() - 15 * 60 * 1000,
      balance: {
        walletId: '4',
        tokens: [],
        totalValueUSD: 9666.62,
        change24h: 1091.43,
        change24hPercent: 12.7,
      },
    },
  ];

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
    toast.success('Direccion copiada');
  };

  const toggleBalanceVisibility = (walletId: string) => {
    setHiddenBalances((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(walletId)) {
        newSet.delete(walletId);
      } else {
        newSet.add(walletId);
      }
      return newSet;
    });
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));

    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="space-y-4">
      {wallets.map((wallet) => {
        const isPositive = wallet.balance.change24hPercent >= 0;
        const isHidden = hiddenBalances.has(wallet.id);
        const providerInfo = WALLET_PROVIDER_INFO[wallet.type];
        const chainInfo = SUPPORTED_CHAINS[wallet.chainId];

        return (
          <Card
            key={wallet.id}
            className={`transition-all ${wallet.isActive ? 'border-violet-500 bg-violet-500/5' : ''}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                {/* Wallet Info */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-2xl">
                      {providerInfo.icon}
                    </div>
                    {wallet.isActive && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-slate-900" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                        {wallet.label}
                      </h3>
                      {wallet.isActive && (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                          Activa
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        style={{ borderColor: chainInfo?.color, color: chainInfo?.color }}
                      >
                        {chainInfo?.name || 'Unknown'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                        {truncateAddress(wallet.address)}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0"
                        onClick={() => copyAddress(wallet.address)}
                      >
                        {copiedAddress === wallet.address ? (
                          <CheckCircle className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {providerInfo.name} · Sincronizado hace {formatTimeAgo(wallet.lastSynced)}
                    </p>
                  </div>
                </div>

                {/* Balance & Actions */}
                <div className="flex items-center gap-6">
                  {/* Balance */}
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        {isHidden ? '••••••' : `$${wallet.balance.totalValueUSD.toLocaleString()}`}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleBalanceVisibility(wallet.id)}
                      >
                        {isHidden ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </Button>
                    </div>
                    {!isHidden && (
                      <p className={`text-sm flex items-center gap-1 justify-end ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                        {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {isPositive ? '+' : ''}{wallet.balance.change24hPercent.toFixed(2)}% (24h)
                      </p>
                    )}
                  </div>

                  {/* Actions Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => console.log('Set active:', wallet.id)}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Establecer como activa
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => console.log('Sync:', wallet.id)}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sincronizar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => console.log('Settings:', wallet.id)}>
                        <Settings className="mr-2 h-4 w-4" />
                        Configuracion
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          window.open(`https://etherscan.io/address/${wallet.address}`, '_blank');
                        }}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Ver en Explorer
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => console.log('Remove:', wallet.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
